import {
  FunctionComponent,
  MouseEventHandler,
  ReactNode,
  RefAttributes,
  forwardRef,
  useState,
} from "react";
import { useTransition } from "transition-hook";
import Loader from "./Spinner";
import Hyperlink from "./Hyperlink";

export type ButtonTheme =
  | "default"
  | "primary"
  | "secondary"
  | "tertiary"
  | "error"
  | "success"
  | "delete"
  | "google";

const Button: FunctionComponent<
  JSX.IntrinsicElements["button"] & {
    xsmall?: boolean;
    small?: boolean;
    medium?: boolean;
    icon?: ReactNode;
    theme?: ButtonTheme;
    href?: string;
    // If true, render a nice loader after the button is clicked
    loaderOnClick?: boolean;
    onClick?: MouseEventHandler;
    block?: boolean;
    disabled?: boolean;
    loading?: boolean;
    focusOnRender?: boolean;
  } & RefAttributes<HTMLButtonElement>
> = forwardRef(function ButtonImpl(props, ref) {
  const {
    icon,
    theme = "default",
    href,
    children,
    loaderOnClick,
    block,
    small,
    xsmall,
    medium,
    className,
    disabled,
    loading: loadingProp,
    ...otherProps
  } = props;

  const [loadingAfterClick, setLoadingAfterClick] = useState(false);
  const loading = loadingAfterClick || loadingProp;
  const loader = useTransition(loading || false, 500);

  // If href is provided, wrap button either in an anchor or Link component
  if (href) {
    const { href: _, ...otherProps } = props;
    return (
      <Hyperlink href={href} className={block ? "block" : ""}>
        <Button {...otherProps} ref={ref} />
      </Hyperlink>
    );
  }
  const themeClasses = {
    primary:
      "text-white border border-transparent bg-[#352990] hover:bg-[#41349e] shadow-md",
    secondary:
      "border border-gray-300 hover:border-gray-400 active:border-gray-300",
    default: "", // will copy `secondary`'s classes into this key in a moment
    tertiary: "border-transparent shadow-none",
    error:
      "text-white border border-transparent bg-red-500 hover:bg-red-400 shadow-md",
    success:
      "text-white border border-transparent bg-green-500 hover:bg-green-400 shadow-md",
    delete:
      "border border-red-800 text-red-800 hover:bg-red-100 hover:bg-opacity-20 active:border-red-800",
    google:
      "text-white border bg-gradient-to-r from-[#0e6be6] to-[#4165e8] border-[#0e6be6] hover:opacity-90 shadow-md",
  };
  themeClasses.default = themeClasses.secondary;

  const sizeClasses = {
    xsmall: {
      button: "px-2 py-1 text-xs shadow-sm active:shadow-none",
      icon: "text-xs mr-0.5",
      loaderSize: 12,
    },
    small: {
      button: "px-2 py-1 text-sm shadow-sm active:shadow-none",
      icon: "text-sm mr-0.5",
      loaderSize: 15,
    },
    medium: {
      button: "px-3 py-2 text-sm shadow-md active:shadow-sm",
      icon: "text-sm mr-1",
      loaderSize: 20,
    },
    large: {
      button: "px-8 py-4 text-md shadow-md active:shadow-sm",
      icon: "text-2xl mr-2",
      loaderSize: 25,
    },
  };

  let size: keyof typeof sizeClasses = "large";
  if (medium) size = "medium";
  if (small) size = "small";
  if (xsmall) size = "xsmall";

  const {
    button: buttonClass,
    icon: iconClass,
    loaderSize,
  } = sizeClasses[size];

  let classes = themeClasses[theme];

  const loadingSpanStyles = {
    transform: "rotateX(45deg) translateY(-1em)",
    opacity: 0,
  };

  return (
    <button
      disabled={disabled}
      className={`rounded transition-all ${classes} relative disabled:opacity-60 ${
        block ? "w-full flex justify-center" : ""
      } ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${buttonClass} ${className}`}
      onMouseUp={loaderOnClick ? () => setLoadingAfterClick(true) : undefined}
      ref={ref}
      {...otherProps}
    >
      <span
        className={`transition-all flex items-center duration-300 justify-center`}
        style={loading ? loadingSpanStyles : undefined}
      >
        {icon && <span className={iconClass}>{icon}</span>}
        {children}
      </span>
      {loader.shouldMount && (
        <span
          className="absolute top-1/2 left-1/2 flex items-center justify-center w-20 h-20 -m-10 transition-all duration-500"
          style={
            loader.stage === "enter"
              ? undefined
              : { transform: "scale(0.5)", opacity: 0 }
          }
        >
          <Loader size={loaderSize} />
        </span>
      )}
    </button>
  );
});

export default Button;
